import { ROLE_LABELS, ROLE_VALUES } from '../enums/role.js';
import { DIRECTORY_BY_CODE, DIRECTORY_REGISTRY } from '../directories/registry.js';
import { SOLVER_WEIGHT_LABELS } from '../solver/defaults.js';
import { ruleKeyDef } from './rules.js';
/** Сутності аудиту, що не є довідниками (entity = ім'я моделі Prisma; BUILD розд. 13 п. 6). */
export const AUDIT_ENTITY_LABELS = {
    '*': 'Усі довідники (одним файлом)',
    User: 'Користувач',
    RoleAssignment: 'Роль користувача',
    RefreshToken: 'Сеанс',
    Schedule: 'Розклад',
    ScheduleVersion: 'Версія розкладу',
    ScheduleEntry: 'Заняття (запис розкладу)',
    EntryException: 'Разова зміна заняття',
    ScheduleChange: 'Журнал змін розкладу',
    GenerationRun: 'Запуск генерації',
    Approval: 'Крок погодження',
    Conflict: 'Конфлікт',
    EmailOutbox: 'Лист у черзі',
    Notification: 'Сповіщення',
    AuditLog: 'Журнал аудиту',
    ImportJob: 'Імпорт',
    PublicToken: 'Публічне посилання',
    Export: 'Вивантаження',
};
const ENTRY_BY_MODEL = new Map(DIRECTORY_REGISTRY.filter((e) => e.kind !== 'dictionary').map((e) => [e.prismaModel, e]));
/** Запис реєстру для сутності аудиту: код довідника або модель Prisma структурного довідника. */
export function auditRegistryEntry(entity) {
    return DIRECTORY_BY_CODE[entity] ?? ENTRY_BY_MODEL.get(entity);
}
/** Людська назва сутності журналу аудиту (для невідомої — саме значення). */
export function auditEntityLabel(entity) {
    if (!entity)
        return '';
    return AUDIT_ENTITY_LABELS[entity] ?? auditRegistryEntry(entity)?.title ?? entity;
}
/** Загальні підписи полів змін (сутності поза реєстром довідників, службові ключі аудиту). */
export const AUDIT_FIELD_LABELS = {
    email: 'Email',
    fullName: 'ПІБ',
    isActive: 'Активний',
    password: 'Пароль',
    roles: 'Ролі',
    teacherId: 'Викладач',
    studentGroupId: 'Група студента',
    status: 'Статус',
    label: 'Підпис',
    note: 'Примітка',
    comment: 'Коментар',
    deletedAt: 'Видалено',
    key: 'Ключ запису',
    rows: 'Рядків',
    sheets: 'Аркушів',
    created: 'Створено',
    updated: 'Оновлено',
    skipped: 'Пропущено',
    errors: 'Помилок',
    attempts: 'Спроб',
    lastError: 'Помилка',
    nextAttemptAt: 'Наступна спроба',
    filters: 'Фільтри',
    count: 'Кількість',
    weights: 'Ваги',
    isDefault: 'За замовчуванням',
    isSystem: 'Системний',
    value: 'Значення',
    scope: 'Область',
    facultyId: 'Факультет',
    departmentId: 'Кафедра',
    course: 'Курс',
    name: 'Назва',
    code: 'Код',
    description: 'Опис',
    weekday: 'День тижня',
    timeSlotId: 'Пара',
    roomId: 'Аудиторія',
    parity: 'Тиждень',
    dateFrom: 'Дата від',
    dateTo: 'Дата до',
    locked: 'Закріплено',
    teacherIds: 'Викладачі',
    qualityScore: 'Показник якості',
    ip: 'IP-адреса',
    userAgent: 'Браузер',
    anonymized: 'Знеособлено записів',
    olderThan: 'Старші за',
};
/**
 * Службове поле змін, яке не є полем запису: рушій довідників пише під час видалення `{ key: [ключ запису, null] }`
 * (для scheduling_rules поле реєстру `key` — «Вид правила», тож підпис реєстру тут хибний).
 */
export function isAuditServiceField(field, action) {
    return action === 'DELETE' && field === 'key';
}
/**
 * Підпис поля зміни в журналі аудиту: поле реєстру довідника (у т. ч. meta.*), вага профілю (weights.<ключ>),
 * ключ правила розкладу; інакше — загальний підпис або саме ім'я. action — дія запису (службовий ключ видалення).
 */
export function auditFieldLabel(entity, field, action) {
    if (isAuditServiceField(field, action))
        return AUDIT_FIELD_LABELS.key;
    const entry = entity ? auditRegistryEntry(entity) : undefined;
    if (entry) {
        const f = entry.fields.find((x) => x.key === field || x.column === field);
        if (f)
            return f.label;
    }
    if (field.startsWith('weights.')) {
        const k = field.slice('weights.'.length);
        if (SOLVER_WEIGHT_LABELS[k])
            return `Вага: ${SOLVER_WEIGHT_LABELS[k]}`;
    }
    const rule = ruleKeyDef(field);
    if (rule)
        return rule.label;
    return AUDIT_FIELD_LABELS[field] ?? field;
}
/** Чи є поле змін переліком ролей користувача (User.roles). */
export function isAuditRolesField(entity, field) {
    return entity === 'User' && field === 'roles';
}
/** Розбір рядка ролі «РОЛЬ|facultyId|departmentId»; не роль — null. */
export function parseAuditRoleKey(v) {
    if (typeof v !== 'string')
        return null;
    const [role = '', facultyId = '', departmentId = ''] = v.split('|');
    if (!ROLE_VALUES.includes(role))
        return null;
    return { role: role, facultyId: facultyId || null, departmentId: departmentId || null };
}
/** Роль людською мовою: «Кафедра: Кафедра анатомії»; без області — лише підпис ролі. */
export function auditRoleText(k, names = {}) {
    const scope = k.departmentId
        ? (names.department?.get(k.departmentId) ?? k.departmentId)
        : k.facultyId
            ? (names.faculty?.get(k.facultyId) ?? k.facultyId)
            : null;
    return scope ? `${ROLE_LABELS[k.role]}: ${scope}` : ROLE_LABELS[k.role];
}
/** Стан облікового запису (User.isActive) — значення фільтра isActive списку «Користувачі й ролі». */
export const USER_ACTIVE_FILTER_VALUES = ['true', 'false'];
/** Підписи фільтра активності (множина). */
export const USER_ACTIVE_FILTER_LABELS = { true: 'Активні', false: 'Заблоковані' };
/** Підписи стану облікового запису (бейдж у таблиці й картці). */
export const USER_ACTIVE_STATE_LABELS = { true: 'Активний', false: 'Заблоковано' };
export const USER_ACTIVE_STATE_TONES = { true: 'success', false: 'danger' };
/** Тони бейджів дій аудиту (enum AuditAction — без тонів). */
export const AUDIT_ACTION_TONES = {
    CREATE: 'success',
    UPDATE: 'info',
    DELETE: 'danger',
    STATUS_CHANGE: 'info',
    IMPORT: 'neutral',
    EXPORT: 'neutral',
    GENERATE: 'info',
    APPROVE: 'success',
    RETURN: 'warning',
    PUBLISH: 'success',
    LOGIN: 'neutral',
};
/** Тони типів сповіщень (дзвіночок, сторінка сповіщень). */
export const NOTIFICATION_TYPE_TONES = {
    SCHEDULE_PUBLISHED: 'success',
    ENTRY_CHANGED: 'info',
    ROOM_CHANGED: 'info',
    TEACHER_CHANGED: 'info',
    ENTRY_MOVED: 'warning',
    ENTRY_CANCELLED: 'danger',
    APPROVAL_REQUIRED: 'warning',
    VERSION_RETURNED: 'danger',
    VERSION_APPROVED: 'success',
    GENERATION_DONE: 'success',
    GENERATION_FAILED: 'danger',
    IMPORT_DONE: 'neutral',
};
//# sourceMappingURL=admin-labels.js.map